import React from 'react';
import { Card, Tag, Button, Dropdown, Tooltip } from 'antd';
import {
  CarOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  ToolOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  AlertOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  PauseCircleOutlined,
  StopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { WorkOrder } from '../../types';
import { formatCurrency } from '../../utils/formatCurrency';

interface WorkOrderCardProps {
  workOrder: WorkOrder;
  onEdit?: (workOrder: WorkOrder) => void;
  onDelete?: (workOrder: WorkOrder) => void;
  onStatusChange?: (workOrder: WorkOrder, newStatus: WorkOrder['status']) => void;
  onCardClick?: (workOrder: WorkOrder) => void;
  isDragging?: boolean;
  dragListeners?: any;
}

const WorkOrderCard: React.FC<WorkOrderCardProps> = ({
  workOrder,
  onEdit,
  onDelete,
  onStatusChange,
  onCardClick,
  isDragging = false,
  dragListeners,
}) => {
  const getPriorityColor = (priority: WorkOrder['priority']) => {
    switch (priority) {
      case 'urgent': return '#ff4d4f';
      case 'high': return '#faad14';
      case 'normal': return '#1890ff';
      case 'low': return '#52c41a';
      default: return '#1890ff';
    }
  };

  const getStatusConfig = (status: WorkOrder['status']) => {
    switch (status) {
      case 'new':
      case 'created':
        return {
          icon: <AlertOutlined style={{ fontSize: 18 }} />,
          color: '#1890ff',
          bgColor: '#e6f7ff',
          text: 'New'
        };
      case 'in_progress':
        return {
          icon: <SyncOutlined spin style={{ fontSize: 18 }} />,
          color: '#fa8c16',
          bgColor: '#fff7e6',
          text: 'In Progress'
        };
      case 'waiting_parts':
        return {
          icon: <PauseCircleOutlined style={{ fontSize: 18 }} />,
          color: '#722ed1',
          bgColor: '#f9f0ff',
          text: 'Waiting Parts'
        };
      case 'waiting_approval':
        return {
          icon: <ClockCircleOutlined style={{ fontSize: 18 }} />,
          color: '#faad14',
          bgColor: '#fffbe6',
          text: 'Needs Approval'
        };
      case 'completed':
        return {
          icon: <CheckCircleOutlined style={{ fontSize: 18 }} />,
          color: '#52c41a',
          bgColor: '#f6ffed',
          text: 'Completed'
        };
      case 'cancelled':
        return {
          icon: <StopOutlined style={{ fontSize: 18 }} />,
          color: '#ff4d4f',
          bgColor: '#fff1f0',
          text: 'Cancelled'
        };
      default:
        return {
          icon: <AlertOutlined style={{ fontSize: 18 }} />,
          color: '#8c8c8c',
          bgColor: '#f5f5f5',
          text: status
        };
    }
  };

  const getCustomerDisplayName = () => {
    if (workOrder.customer?.company_name) {
      return workOrder.customer.company_name;
    }
    if (workOrder.customer) {
      return `${workOrder.customer.first_name} ${workOrder.customer.last_name}`;
    }
    return 'Unknown Customer';
  };

  const getVehicleDisplayName = () => {
    if (workOrder.vehicle) {
      return `${workOrder.vehicle.year} ${workOrder.vehicle.make} ${workOrder.vehicle.model}`;
    }
    return 'Unknown Vehicle';
  };

  const handleMenuClick = (e: any) => {
    e.domEvent?.stopPropagation();

    const key = e.key;

    if (key === 'edit') {
      onEdit?.(workOrder);
    } else if (key === 'delete') {
      onDelete?.(workOrder);
    } else if (key === 'new' || key === 'in_progress' || key === 'waiting_parts' || key === 'waiting_approval' || key === 'completed') {
      onStatusChange?.(workOrder, key as WorkOrder['status']);
    }
  };

  const actionMenuItems = [
    {
      key: 'edit',
      label: 'Edit Work Order',
      icon: <EditOutlined />,
    },
    {
      key: 'status',
      label: 'Change Status',
      icon: <SyncOutlined />,
      children: [
        { key: 'new', label: 'New' },
        { key: 'in_progress', label: 'In Progress' },
        { key: 'waiting_parts', label: 'Waiting Parts' },
        { key: 'waiting_approval', label: 'Waiting Approval' },
        { key: 'completed', label: 'Completed' },
      ],
    },
    { type: 'divider' as const },
    {
      key: 'delete',
      label: 'Unapprove',
      icon: <DeleteOutlined />,
      danger: true,
    },
  ];

  const estimatedDays = workOrder.estimated_completion
    ? Math.ceil((new Date(workOrder.estimated_completion).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('.ant-dropdown-trigger') || target.closest('.ant-dropdown-menu')) {
      return;
    }
    onCardClick?.(workOrder);
  };

  const statusConfig = getStatusConfig(workOrder.status);

  return (
    <Card
      className={`work-order-card transition-all duration-200 ${isDragging ? 'opacity-50 scale-105 shadow-2xl' : 'hover:shadow-lg'
        }`}
      styles={{ body: { padding: 0 } }}
      style={{
        borderRadius: 8,
        marginBottom: 0,
        overflow: 'hidden',
        border: `1px solid ${getPriorityColor(workOrder.priority)}30`,
        cursor: 'pointer',
      }}
      onClick={handleCardClick}
    >
      {/* Priority Bar */}
      <div
        style={{
          height: 3,
          background: `linear-gradient(90deg, ${getPriorityColor(workOrder.priority)} 0%, ${getPriorityColor(workOrder.priority)}80 100%)`,
        }}
      />

      <div style={{ padding: '14px' }}>
        {/* Header with Status Badge */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 flex-1">
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                backgroundColor: statusConfig.bgColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: statusConfig.color,
                flexShrink: 0,
              }}
            >
              {statusConfig.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="font-bold text-gray-900 truncate cursor-grab active:cursor-grabbing"
                style={{ fontSize: 14, marginBottom: 1, lineHeight: '1.3' }}
                {...dragListeners}
                title="Drag to move"
              >
                {workOrder.wo_number}
              </div>
              <div style={{ fontSize: 11, color: statusConfig.color, fontWeight: 600 }}>
                {statusConfig.text}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1" style={{ marginLeft: 4 }}>
            <Tag
              color={getPriorityColor(workOrder.priority)}
              style={{ margin: 0, fontSize: 10, padding: '1px 6px', lineHeight: '18px', fontWeight: 600 }}
            >
              {workOrder.priority.toUpperCase()}
            </Tag>
            <Dropdown
              menu={{ items: actionMenuItems, onClick: handleMenuClick }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                type="text"
                icon={<MoreOutlined />}
                size="small"
                style={{ width: 24, height: 24, padding: 0, fontSize: 14 }}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          </div>
        </div>

        {/* Customer & Vehicle */}
        <div style={{ marginBottom: 10, padding: '9px', backgroundColor: '#fafafa', borderRadius: 6 }}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <UserOutlined style={{ fontSize: 11, color: '#8c8c8c' }} />
            <span style={{ fontSize: 12, color: '#262626', fontWeight: 500, lineHeight: '1.3' }} className="truncate">
              {getCustomerDisplayName()}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <CarOutlined style={{ fontSize: 11, color: '#8c8c8c' }} />
            <span style={{ fontSize: 11, color: '#595959', lineHeight: '1.3' }} className="truncate">
              {getVehicleDisplayName()}
            </span>
          </div>
        </div>

        {/* Problem Description */}
        <div style={{ marginBottom: 10 }}>
          <p
            style={{
              fontSize: 12,
              color: '#595959',
              lineHeight: '1.4',
              margin: 0,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {workOrder.problem_description}
          </p>
        </div>

        {/* Footer Info - Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
            paddingTop: 10,
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <Tooltip title="Total Amount">
            <div className="flex items-center gap-1.5">
              <DollarOutlined style={{ fontSize: 13, color: '#52c41a' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#262626', lineHeight: '1.2' }}>
                {formatCurrency(workOrder.total_amount)}
              </span>
            </div>
          </Tooltip>

          {workOrder.assigned_technician ? (
            <Tooltip title="Assigned Technician">
              <div className="flex items-center gap-1.5">
                <ToolOutlined style={{ fontSize: 13, color: '#1890ff' }} />
                <span style={{ fontSize: 11, color: '#595959', lineHeight: '1.2' }} className="truncate">
                  {workOrder.assigned_technician}
                </span>
              </div>
            </Tooltip>
          ) : (
            <div></div>
          )}

          {estimatedDays !== null && (
            <Tooltip title="Estimated Completion">
              <div className="flex items-center gap-1.5">
                <ClockCircleOutlined style={{ fontSize: 13, color: '#faad14' }} />
                <span style={{ fontSize: 11, color: '#595959', lineHeight: '1.2' }}>
                  {estimatedDays > 0 ? `${estimatedDays}d` : 'Due'}
                </span>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </Card>
  );
};

export default WorkOrderCard;
